import { type ProductSelectDto, UserProfileRole } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { cn } from '@lib/utils';
import { useGetProducts } from '@queries/product.query';
import { useGetTechnology } from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductContainer } from './product-container.component';
import { ProductFormModal } from './product-form-modal.component';

type ProductsGridTileProps = {
  className?: string;
  hideIfEmpty?: boolean;
  productIds?: string[];
  products?: ProductSelectDto[];
  title?: string;
  technologyId?: string;
};

export const ProductsGridTile: FunctionComponent<ProductsGridTileProps> = (props) => {
  const {
    className,
    hideIfEmpty = false,
    productIds,
    products: providedProducts,
    technologyId,
    title = 'Products',
  } = props;
  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();
  const { data: technology } = useGetTechnology(technologyId);
  const { data: queriedProducts, isLoading: isProductsPending } = useGetProducts(technologyId, {
    enabled: !providedProducts,
  });
  const sourceProducts = providedProducts ?? queriedProducts;
  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });

  const filteredProducts = sourceProducts?.filter((product) => {
    if (productIds) {
      return productIds.includes(product.id);
    }

    return technologyId ? product.technologies?.some((tech) => tech.id === technologyId) : true;
  });

  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isViewingTechnology = Boolean(technologyId);
  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;
  const allowAddProduct = isInnovator && !isViewingTechnology && !productIds && !providedProducts;
  const hasProducts = Boolean(filteredProducts && filteredProducts.length > 0);
  const isLoading = providedProducts ? false : isProductsPending;

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);

    if (!isViewingTechnology) {
      navigate('/products');
    }
  };

  const onNavigateToDetails = (id: string): void => {
    navigate(`/product/${id}`);
  };

  if (hideIfEmpty && !hasProducts) {
    return null;
  }

  return (
    <>
      <Tile
        className={cn('w-full', (!hasProducts || isLoading) && 'min-h-[260px]', className)}
        emptyState={{
          title: 'No Products',
          message: allowAddProduct ? 'Add Product' : undefined,
          onClick: allowAddProduct ? openModal : undefined,
        }}
        header={{
          button: allowAddProduct
            ? {
                onClick: openModal,
                text: '+ Add',
              }
            : undefined,
          title,
        }}
        isEmpty={!hasProducts}
        isLoading={isLoading}
      >
        <div className="flex flex-col gap-4">
          {filteredProducts?.map((product) => (
            <ProductContainer
              hideHover={isChildOrganizationView}
              key={product.id}
              onNavigateToDetails={onNavigateToDetails}
              product={product}
            />
          ))}
        </div>
      </Tile>

      {isModalOpen && isInnovator && (
        <ProductFormModal
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </>
  );
};
